/**
 * Order module types matching backend DTOs.
 *
 * Sources:
 *   - OrderResponse, OrderService, Discount, PaymentMethod, Appointment,
 *     PropertyAccess, ParentOrder, OrderPropertyResponse, OrderPageResponse
 *     — order-service  (already defined in property.types.ts, re-exported here)
 *   - ProductService, ServicePricing — products-api (product-service)
 *   - SavedCard, CreateCardRequest, UpdateCardRequest — entity-service (card endpoints)
 *   - Promocode — order-service promo endpoint
 *   - OrderBulkUploadRequest, OrderBulkUploadRow — order-service bulk upload
 *   - CollectPaymentDTO, OrderRequest, BulkOrderRequest — order-service placement
 *   - EntityHierarchy — entity-service tree endpoint
 */

import type { AccessType } from './property.types';
import type { AssetRef } from '../components/orders/cart/types/cart.types';

// Re-export order DTOs that already live in property.types.ts so consumers
// can import everything order-related from one place.
export type {
  OrderService,
  OrderDiscount,
  PaymentMethod,
  PropertyAccess,
  Appointment,
  ParentOrder,
  OrderPropertyResponse,
  OrderResponse,
  OrderPageResponse,
  ServiceSummaryDTO,
  OrderFilterMetadataResponse,
} from './property.types';

// Re-export AccessType for convenience (used by OrderPropertyAccess below)
export type { AccessType } from './property.types';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * Order lifecycle statuses — raw backend values from the `orderStatus` field
 * on `OrderResponse` (order-service).
 *
 * Note: The existing `OrderStatusEnum` in
 * `components/orders/table-cell/model/orderCell.ts` uses UI-prefixed values
 * like `order.received` / `appointment.scheduled` for display badge mapping.
 * Those are handled separately; this enum holds the canonical API values.
 */
export enum OrderStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  RECEIVED = 'received',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  DELIVERED = 'delivered',
  VERIFIED = 'verified',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  UNABLE_TO_COMPLETE = 'unable_to_complete',
}

/** Human-readable labels for each OrderStatus */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.DRAFT]: 'Draft',
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.RECEIVED]: 'Order Received',
  [OrderStatus.SCHEDULED]: 'Appointment Scheduled',
  [OrderStatus.IN_PROGRESS]: 'In Progress',
  [OrderStatus.DELIVERED]: 'Order Delivered',
  [OrderStatus.VERIFIED]: 'Verified',
  [OrderStatus.COMPLETED]: 'Completed',
  [OrderStatus.CANCELLED]: 'Cancelled',
  [OrderStatus.UNABLE_TO_COMPLETE]: 'Unable to Complete',
};

/** Discount type on a promo code or line-item discount */
export enum DiscountType {
  FIXED_DISCOUNT = 'fixed_discount',
  PERCENT_DISCOUNT = '%_discount',
}

/** Card brand identifiers returned by payment gateway */
export enum CardType {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER',
  UNKNOWN = 'UNKNOWN',
}

/** Card expiry status returned by entity-service */
export enum CardExpiryStatus {
  VALID = 'Valid',
  EXPIRING_SOON = 'Expiring soon',
  EXPIRED = 'Expired',
}

// ---------------------------------------------------------------------------
// Order Service DTOs — aliases for clarity
// ---------------------------------------------------------------------------

/**
 * Property access instructions attached to an order.
 *
 * This is a richer version of the PropertyAccess DTO in property.types.ts,
 * using the AccessType enum for the accessType field.
 */
export interface OrderPropertyAccess {
  accessType: AccessType | null;
  accessCode: string | null;
  lockboxLocation: string | null;
  onsiteContactName: string | null;
  onsiteContactPhone: string | null;
  propertyVacant: string | null;
  rentlyRegistrationPin: string | null;
  gateCode: string | null;
  accessLocation: string | null;
}

/**
 * Appointment DTO with field names matching the backend exactly.
 *
 * Note: The existing Appointment type in property.types.ts uses slightly
 * different field names (durationMinutes vs durationM, cancelation vs
 * cancellation). This alias provides the exact backend field names.
 */
export interface OrderAppointment {
  appointmentUUID: string | null;
  appointmentId: number | null;
  startUtc: string | null;
  endUtc: string | null;
  dateStartLocal: string | null;
  timeStartLocal: string | null;
  timeEndLocal: string | null;
  durationM: number | null;
  planotechName: string | null;
  cancellationReason: string | null;
  cancellationNotes: string | null;
  cancelled: boolean | null;
}

/**
 * Payment method on an order (alias of PaymentMethod from property.types.ts).
 * Re-exported above — this named alias exists for code that prefers the
 * `OrderPaymentMethod` name.
 */
export type { PaymentMethod as OrderPaymentMethod } from './property.types';

// ---------------------------------------------------------------------------
// Payment collection DTO
// ---------------------------------------------------------------------------

/** Request body for POST /order-service/api/v1/orders/payment/collect */
export interface CollectPaymentDTO {
  amount: number;
  tax: number;
  cardNumber: string | null;
  cardType: string | null;
  orderNumber: string;
  customerProfileId: string;
  customerPaymentProfileId: string;
  // DV-2789: no CVV — charge/validate run against the stored Authorize.net profile, which doesn't
  // need it, so we never forward one from a saved card (PCI-DSS 3.3.3).
}

// ---------------------------------------------------------------------------
// Order placement DTOs
// ---------------------------------------------------------------------------

/** Service line item in an order request */
export interface OrderRequestService {
  slug?: string | null;
  shoppingCartSlug?: string | null; // asset/property orders key off this
  quantity: number | null;
}

/** Property access subset used in order request body */
export interface OrderRequestPropertyAccess {
  isOnsiteAppointment?: boolean | null;
  onsiteContactName?: string | null;
  onsiteContactPhone?: string | null;
  isAccessNeeded?: boolean | null;
  accessType?: string | null;
  lockboxLocation?: string | null;
  accessCode?: string | null;
  rentlyRegistrationPin?: string | null;
  preferredAppointmentDate?: string | null;
  gateCode?: string | null;
}

/** Payment sub-object inside an order request */
export interface OrderRequestPayment {
  tax: number;
  customerProfileId: string;
  customerPaymentProfileId: string;
  // DV-2789: no CVV — see CollectPaymentDTO.
}

/** Request body for POST /order-service/api/v1/orders */
export interface OrderRequest {
  propertyUUID?: string | null;
  property?: OrderRequestProperty | null;
  promocode?: string | null;
  orderedBy?: string | null;
  userId?: string | null;
  entity?: string | null;
  entityUUID?: string | null;
  specialInstructions?: string | null;
  propertyId?: string | null;
  beds?: number | null;
  baths?: number | null;
  firstAvailableDate?: string | null;
  squareFeet?: number | null;
  services?: OrderRequestService[];
  propertyAccess?: OrderRequestPropertyAccess;
  payment?: OrderRequestPayment | null;
  cardNumber?: string | null;
  cardType?: string | null;
  // DV-2131 asset orders (POST /orders, single service over N photos)
  assetRefs?: AssetRef[];
  assetNote?: string | null;
  sourceOrderUUID?: string | null;
}

/** Inline property object used in OrderRequest */
export interface OrderRequestProperty {
  address1: string;
  unitNumber?: string | null;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  squareFeet?: number | null;
}

/** Request body for POST /order-service/api/v1/orders/bulk */
export interface BulkOrderRequest {
  promocode?: string | null;
  cardNumber?: string | null;
  cardType?: string | null;
  payment?: OrderRequestPayment | null;
  orders: OrderRequest[];
}

// ---------------------------------------------------------------------------
// Products API DTOs
// ---------------------------------------------------------------------------

/** Pricing breakdown for a specific quantity tier */
export interface ServicePricing {
  quantity: number;
  basePrice: number;
  initialBasePrice: number;
  addOnBasePrice: number;
  unitPrice: number;
  initialUnitPrice: number;
  addOnUnitPrice: number;
  totalPrice: number;
  totalAddOnPrice: number;
  totalInitialPrice: number;
}

/** Full service response from GET /product-service/api/v1/services */
export interface ProductService {
  uuid: string;
  serviceGroupUUID: string | null;
  primaryServicePriority: number | null;
  name: string;
  /**
   * products-api's `Boolean active`, so null is possible. Only `true` is
   * offerable — products-api's own active check (`ActiveTrue`) treats null
   * as inactive too. Read it through `isActiveService` (`util/service-catalog`);
   * never coerce.
   */
  active: boolean | null;
  connectSlug: string | null;
  shoppingCartSlug: string;
  onlineImage: string | null;
  description: string | null;
  onlineDescription: string | null;
  onlineSortOrder: number | null;
  mostPopular: boolean;
  availableOccupied: boolean;
  orderableOnline: boolean;
  orderableApi: boolean;
  requiresInstructions: boolean;
  defaultQuantity: number;
  validQuantities: number[];
  defaultPricing: ServicePricing | null;
  map: Record<string, ServicePricing> | null;
  serviceType: string | null;
  /**
   * DV-2850: does this service produce photos? Sourced from Legacy's
   * `properties.requiresTour` (`service_groups.tour_needed`), flattened by the
   * adapter. THREE-valued on purpose: true, false, and null meaning Legacy has
   * never sent a value — null must stay distinct from false so the catalogue
   * audit can find those services. Read `=== true`; never coerce.
   * Optional: absent on a products-api that predates the field.
   */
  requiresTour?: boolean | null;
  prerequisiteSlugs: string[] | null;
  /**
   * DV-3206: an order carrying this service gets the free Basic Floor Plan
   * line, added by order-service when the order is created. Owned by Connect
   * (Legacy never sends it). Null or absent means false, so read `=== true`.
   */
  includesFreeFloorPlan?: boolean | null;
}

/** Request body for POST /product-service/api/v1/services/quote */
export interface QuoteRequest {
  items: { shoppingCartSlug: string; quantity: number; occupancyStatus?: string | null }[];
}

/** One priced line in a quote response */
export interface QuoteLine {
  shoppingCartSlug: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  initialLineTotal: number;
}

/** Response from POST /product-service/api/v1/services/quote */
export interface QuoteResponse {
  lines: QuoteLine[];
  subtotal: number;
  initialSubtotal: number;
  total: number;
}

/**
 * GET /order-service/api/v1/orders/free-floor-plan/eligibility?entityUUID=
 * (DV-3609): would an order for this account get the free Basic Floor Plan
 * line? Decided by the same code as order creation.
 */
export interface FreeFloorPlanEligibility {
  /**
   * true: an order carrying a package flagged `includesFreeFloorPlan` gets the
   * line. false: it does not (rollout off, account not covered, or Basic
   * inactive). null: order-service could not decide — always present.
   */
  offered: boolean | null;
  /**
   * Products that already give an order a floor plan, so it gets no free line
   * beside them: the free slug first, then the rollout's list. Trimmed and
   * lower-cased.
   */
  floorPlanSlugs: string[];
}

// The platform groups services into two categories, each given its own pill
// hue in the orders table: "property" services where we visit the property
// (Photography, Zillow 3D, ...) and "asset" services where we edit an existing
// asset (AI Virtual Staging, Large Object Removal, ...). The backend tags asset
// services with serviceType "asset" (the `servicesType=asset` filter); anything
// else — including legacy/unknown slugs — reads as a property service.
export type ServiceCategory = 'property' | 'asset';

/** Extended service with UI-only fields used in the service selection step */
export interface DisplayService extends ProductService {
  type: string;
  selectedQuantity: number;
  selectedPrice: number;
  isSelected: boolean;
  quantityOptions: number[];
}

// ---------------------------------------------------------------------------
// Payment / Card DTOs (entity-service)
// ---------------------------------------------------------------------------

/** Saved card response from GET /entity-service/api/v1/customer/card */
export interface SavedCard {
  id: string;
  customerPaymentProfileId: string;
  customerProfileId: string;
  firstName: string;
  lastName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  nickName: string | null;
  phone: string | null;
  cardNumber: string;
  // DV-2789: CVV is not stored on a saved card (a fresh CVV is only collected at create time via
  // CreateCardRequest, used for the auth call, and discarded — never read back).
  expirationMonth: string;
  expirationYear: string;
  entity: string | null;
  entityUUID: string | null;
  cardType: CardType | string;
  default: boolean;
  cardExpiryStatus: CardExpiryStatus | string;
}

/** Response envelope from GET /entity-service/api/v1/customer/card */
export interface SavedCardsResponse {
  cards: SavedCard[];
}

/** Request body for POST /entity-service/api/v1/customer/card (create) */
export interface CreateCardRequest {
  cardNumber: number;
  cardCode: string;
  firstName: string;
  cardType: string;
  zipCode: string;
  expirationMonth: string;
  expirationYear: string;
  entity: string | null;
  isDefault: boolean;
}

/** Request body for PUT /entity-service/api/v1/customer/card (update) */
export interface UpdateCardRequest {
  customerPaymentProfileId: string;
  customerProfileId: string;
  firstName: string;
  expirationMonth: string;
  expirationYear: string;
  entity: string | null;
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Promo Code
// ---------------------------------------------------------------------------

/** Promo code response from GET /order-service/api/v1/orders/promocodes */
export interface Promocode {
  promocodeId: string;
  promocode: string;
  description: string | null;
  discountType: DiscountType | string;
  discountValue: number;
  startDate: string;
  endDate: string;
  usageLimit: number;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Bulk Import DTOs
// ---------------------------------------------------------------------------

/** Bulk upload file record from GET /order-service/api/v1/orders/bulk/requests */
export interface OrderBulkUploadRequest {
  fileId: string;
  fileNumber: number | null;
  fileName: string;
  uploadedBy: string | null;
  entityUUID: string | null;
  uploadedDate: string | null;
  updatedAt: string | null;
  orderApproved: number;
  orderRejected: number;
  orderPlaced: number;
  subTotal: number;
}

/** Individual row inside a bulk upload file (includes error info) */
export interface OrderBulkUploadRow {
  id: string;
  fileId: string;
  propId: string | null;
  address1: string | null;
  unitNumber: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  propertyType: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  services: string[] | null;
  accessType: string | null;
  accessCode: string | null;
  lockboxLocation: string | null;
  onsiteContactName: string | null;
  onsiteContactPhone: string | null;
  rentlyRegistrationPin: string | null;
  gateCode: string | null;
  specialInstructions: string | null;
  preferredAppointmentDate: string | null;
  firstAvailableDate: string | null;
  entity: string | null;
  entityUUID: string | null;
  propertyId: string | null;
  previousOrderId: string | null;
  status: string | null;
  errorReasons: string[] | null;
}

// ---------------------------------------------------------------------------
// UI Types (order wizard)
// ---------------------------------------------------------------------------

/** Access + scheduling info collected from step 3 (Order Summary) */
export interface OrderSummaryInfo {
  isOnsiteAppointment: boolean;
  onsiteContactName: string;
  onsiteContactPhone: string;
  isAccessNeeded: boolean;
  accessType: string;
  accessCode: string;
  lockboxLocation: string;
  rentlyRegistrationPin: string;
  gateCode: string;
  specialInstructions: string;
  useFirstAvailableDate: boolean;
  preferredDate: string;
  preferredTime: string;
  propertyVacant: string;
  accessLocation: string;
}

/** Service data passed between wizard steps */
export interface SelectedServiceData {
  services: DisplayService[];
  occupancyStatus: string;
  totalPrice: number;
  totalInitialPrice: number;
  totalSavingPrice: number;
}

/** Payment info from step 4 */
export interface PaymentInfo {
  card: SavedCard;
  promocode: string | null;
  promocodeId: string | null;
  totalAmount: number;
  subTotalAmount: number;
  savingsAmount: number;
}

// ---------------------------------------------------------------------------
// Entity hierarchy (entity-service tree)
// ---------------------------------------------------------------------------

/**
 * entity-service `Integrations` as it arrives on the wire: snake_case, because
 * the backend class pins each field with `@JsonProperty` (its camelCase
 * `@JsonAlias`es apply to input only). Every list is nullable.
 */
export interface EntityIntegrations {
  integration_slugs?: string[] | null;
  integration_webhooks?: string[] | null;
  custom_integration_slugs?: string[] | null;
  custom_integration_webhooks?: string[] | null;
}

/**
 * Recursive entity tree node — raw backend API response from
 * `GET /entity-service/api/v1/entities/{id}`.
 *
 * This is NOT the transformed localStorage shape used by entity dropdowns,
 * which maps to `{ value, label }` pairs. It lives in the order module
 * because the order wizard fetches entity trees for org-scoped placement.
 */
export interface EntityHierarchy {
  entityId: string;
  entityName: string;
  children?: EntityHierarchy[];
  // Optional fields the backend `EntityHierarchyResponse` returns; surfaced
  // here because deep-link entity-resolution walks the tree looking at
  // `customerEntityId` and `integrations`, and the entity switcher derives
  // per-entity watermark availability from `watermarkImage`. The ancestor
  // walk that resolves a deep-linked entity's chain of parents reads
  // `parentEntityUuid`. The full backend DTO has many more fields (logoUrl,
  // status, settings, etc.) — only what's actually consumed is included.
  customerEntityId?: string;
  integrations?: EntityIntegrations | null;
  watermarkImage?: string | null;
  parentEntityUuid?: string;
}

/**
 * Transformed entity tree shape — what app-header writes to localStorage
 * via `convertToTreeNode`, what `tree-select` accepts as its `nodes` prop,
 * and what the `services/entity-tree` helpers (`findEntityNode`,
 * `flattenEntityTree`, etc.) walk. Distinct from `EntityHierarchy`: this uses
 * `value` / `label` / `children` because react-checkbox-tree consumes that
 * key shape.
 *
 * The optional fields (`className`, `plainLabel`, `showCheckbox`) are
 * decorations that `tree-select` adds to nodes during filter/render —
 * carried on the same type so we don't fork into two near-identical
 * shapes.
 */
export interface EntityTreeNode {
  value: string;
  label: string;
  children?: EntityTreeNode[];
  customerEntityId?: string;
  // Mirrors `EntityHierarchy.integrations`; read by `hasCustomWebhookEndpoints`.
  integrations?: EntityIntegrations | null;
  className?: string;
  plainLabel?: string;
  showCheckbox?: boolean;
  // Per-entity watermark availability, derived from `EntityHierarchy.watermarkImage`
  // in `convertToTreeNode`. Lets the entity switcher write a real watermark
  // signal on sub-entity switches instead of failing open.
  hasWatermark?: boolean;
}
